import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectorRef,
  AfterViewInit,
  NgZone,
  ElementRef,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { DesignService } from "@app/core/services/design.service";
import { Router, NavigationEnd } from "@angular/router";
import { Subscription } from "rxjs";

interface FloatItem {
  id: number;
  type:
    | "TR"
    | "BLOCK"
    | "GROUP"
    | "APPLE"
    | "CHOCOLATE"
    | "COFEE"
    | "OREO"
    | "POPCKORN";
  size: number; // px
  x: number;
  y: number;
  vx: number;
  vy: number;
  blur: number; // px
  opacity: number;
  angle: number; // degrees
  // CSS-mode drift params
  sx?: number;
  sy?: number;
  ex?: number;
  ey?: number;
  durS?: number; // seconds
  delayS?: number; // seconds (can be negative)
  row?: number;
}

@Component({
  selector: "app-background-modern",
  templateUrl: "./background-modern.component.html",
  styleUrls: ["./background-modern.component.scss"],
})
export class BackgroundModernComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public items: FloatItem[] = [];
  // Motion mode: 'css' - keyframe drift (no collisions), 'js' - simulation with collisions
  public mode: "css" | "js" = "css";
  // list of active types to render; modify in code for debugging which icons are shown
  public activeTypes: Array<
    | "TR"
    | "BLOCK"
    | "GROUP"
    | "APPLE"
    | "CHOCOLATE"
    | "COFEE"
    | "OREO"
    | "POPCKORN"
  > = [];
  // map type -> filename for template
  public iconMap: { [k: string]: string } = {};
  private rafId: number | null = null;
  private lastTime = 0;
  public running = false;
  private resizeTimeout: any = null;

  @ViewChildren("bgItem") private elItems!: QueryList<ElementRef<HTMLElement>>;
  private elById = new Map<number, HTMLElement>();
  private routeSub?: Subscription;

  constructor(
    private design: DesignService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private router: Router
  ) {}

  ngOnInit(): void {
    // init 15 TR + 15 BLOCK = 30
    this.items = [];
    const w = window.innerWidth;
    const h = window.innerHeight;

    const make = (
      id: number,
      type:
        | "TR"
        | "BLOCK"
        | "GROUP"
        | "APPLE"
        | "CHOCOLATE"
        | "COFEE"
        | "OREO"
        | "POPCKORN",
      size: number,
      blur: number,
      opacity: number,
      row: number
    ) => {
      // random start/end for CSS drift
      const sx = Math.random() * (w - size);
      const sy = Math.random() * (h - size);
      const ex = Math.random() * (w - size);
      const ey = Math.random() * (h - size);
      const durS = 25 + Math.random() * 35; // 25s - 60s
      const delayS = -Math.random() * durS; // negative to randomize phase
      return {
        id,
        type,
        size,
        x: sx,
        y: sy,
        angle: Math.random() * 360,
        // reduce overall speed factor slightly so smaller sizes don't move too fast
        vx: (Math.random() * 2 - 1) * (20 / size) * 20, // slightly slower overall speed
        vy: (Math.random() * 2 - 1) * (20 / size) * 20,
        blur: blur + Math.random() * 1.5, // small jitter for more natural softness
        opacity,
        row,
        sx,
        sy,
        ex,
        ey,
        durS,
        delayS,
      } as FloatItem;
    };

    // TR group: tuned sizes/blur/opacity to match mock (third row removed)
    for (let i = 0; i < 5; i++) this.items.push(make(i, "TR", 35, 0, 1, 1));
    for (let i = 5; i < 10; i++) this.items.push(make(i, "TR", 35, 2, 1, 2));

    // BLOCK group: tuned (third row removed)
    for (let i = 10; i < 15; i++) this.items.push(make(i, "BLOCK", 54, 0, 0.25, 1));
    for (let i = 15; i < 20; i++) this.items.push(make(i, "BLOCK", 54, 2, 0.25, 2));

    // GROUP group: tuned (second row removed)
    for (let i = 20; i < 25; i++) this.items.push(make(i, "GROUP", 54, 0, 0.25, 1));

    // APPLE group: tuned (second row for GROUP removed — indices shifted)
    for (let i = 25; i < 30; i++) this.items.push(make(i, "APPLE", 60, 0, 0.25, 1));
    for (let i = 30; i < 35; i++) this.items.push(make(i, "APPLE", 60, 2, 0.25, 2));

    // CHOCOLATE: tuned (indices shifted)
    for (let i = 35; i < 40; i++)
      this.items.push(make(i, "CHOCOLATE", 54, 0, 0.25, 1));
    for (let i = 40; i < 45; i++)
      this.items.push(make(i, "CHOCOLATE", 54, 2, 0.25, 2));

    // COFEE: tuned (indices shifted)
    for (let i = 45; i < 50; i++) this.items.push(make(i, "COFEE", 54, 0, 0.25, 1));
    for (let i = 50; i < 55; i++) this.items.push(make(i, "COFEE", 54, 2, 0.25, 2));

    // OREO: tuned (indices shifted)
    for (let i = 55; i < 60; i++) this.items.push(make(i, "OREO", 54, 0, 0.25, 1));
    for (let i = 60; i < 65; i++) this.items.push(make(i, "OREO", 54, 2, 0.25, 2));

    // POPCKORN: tuned (indices shifted)
    for (let i = 65; i < 70; i++)
      this.items.push(make(i, "POPCKORN", 54, 0, 0.25, 1));
    for (let i = 70; i < 75; i++)
      this.items.push(make(i, "POPCKORN", 54, 2, 0.25, 2));

    // Additional TR icons: five small icons, no blur, low opacity
    for (let i = 75; i < 80; i++) this.items.push(make(i, "TR", 35, 0, 0.25, 2));

    // activeTypes controls which types will be visible; can be modified for debugging
    this.activeTypes = [
      "TR",
      "BLOCK",
      "GROUP",
      "APPLE",
      "CHOCOLATE",
      "COFEE",
      "OREO",
      "POPCKORN",
    ];

    // mapping of type -> filename in assets
    this.iconMap = {
      TR: "TR.png",
      BLOCK: "BLOCK.png",
      GROUP: "Group.png",
      APPLE: "APPLE.png",
      CHOCOLATE: "CHOCOLATE.png",
      COFEE: "COFEE.png",
      OREO: "OREO.png",
      POPCKORN: "POPCKORN.png",
    } as { [k: string]: string };

    // Start later when view is ready
    window.addEventListener("resize", this.onResize);

    // Determine mode based on current URL
    this.setModeFromUrl(this.router.url);
    // React to future navigations
    this.routeSub = this.router.events.subscribe((ev) => {
      if (ev instanceof NavigationEnd)
        this.setModeFromUrl(ev.urlAfterRedirects || ev.url);
    });
  }

  ngAfterViewInit(): void {
    // build element id map
    this.rebuildElementMap();
    this.elItems.changes.subscribe(() => this.rebuildElementMap());

    // initialize CSS variables once
    this.applyStyles();

    if (this.mode === "js") {
      this.lastTime = performance.now();
      this.running = true;
      this.zone.runOutsideAngular(() => {
        this.rafId = requestAnimationFrame(this.loop.bind(this));
      });
    }
  }

  private onResize = () => {
    // Debounce resize — перераспределяем иконки только после окончания ресайза
    // и только в JS-режиме (главная страница)
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    if (this.mode === 'js') {
      // Во время ресайза просто зажимаем к границам
      const w = window.innerWidth;
      const h = window.innerHeight;
      this.items.forEach((it) => {
        if (it.x > w - it.size) it.x = Math.max(0, w - it.size);
        if (it.y > h - it.size) it.y = Math.max(0, h - it.size);
      });
      this.applyStyles();
      
      // После окончания ресайза — полное перераспределение
      this.resizeTimeout = setTimeout(() => {
        this.redistributeItems();
        this.resizeTimeout = null;
      }, 300);
    }
  };

  /**
   * Равномерно распределяет иконки по текущему размеру экрана
   * и перезапускает анимацию
   */
  private redistributeItems() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    
    // Группируем по размеру для равномерного распределения
    const sizeGroups = new Map<number, FloatItem[]>();
    for (const it of this.items) {
      if (!sizeGroups.has(it.size)) {
        sizeGroups.set(it.size, []);
      }
      sizeGroups.get(it.size)!.push(it);
    }
    
    // Для каждой группы — равномерная сетка с небольшим случайным смещением
    for (const [size, group] of sizeGroups) {
      const count = group.length;
      // Вычисляем сетку: cols x rows
      const aspect = w / h;
      const cols = Math.ceil(Math.sqrt(count * aspect));
      const rows = Math.ceil(count / cols);
      
      const cellW = w / cols;
      const cellH = h / rows;
      
      group.forEach((it, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        
        // Центр ячейки + случайное смещение до 30% от размера ячейки
        const jitterX = (Math.random() - 0.5) * cellW * 0.6;
        const jitterY = (Math.random() - 0.5) * cellH * 0.6;
        
        it.x = Math.max(0, Math.min(w - size, col * cellW + cellW / 2 - size / 2 + jitterX));
        it.y = Math.max(0, Math.min(h - size, row * cellH + cellH / 2 - size / 2 + jitterY));
        
        // Новые случайные скорости
        it.vx = (Math.random() * 2 - 1) * (20 / size) * 20;
        it.vy = (Math.random() * 2 - 1) * (20 / size) * 20;
      });
    }
    
    // Пересобираем element map и применяем стили
    this.rebuildElementMap();
    this.applyStyles();
    
    // Перезапускаем loop если не запущен
    if (!this.running) this.startLoop();
  }

  private loop(now: number) {
    const dt = Math.min(50, now - this.lastTime) / 1000; // seconds
    this.lastTime = now;
    const w = window.innerWidth;
    const h = window.innerHeight;

    for (const it of this.items) {
      it.x += it.vx * dt;
      it.y += it.vy * dt;

      // reflect on walls
      if (it.x <= 0) {
        it.x = 0;
        it.vx = Math.abs(it.vx);
      } else if (it.x + it.size >= w) {
        it.x = w - it.size;
        it.vx = -Math.abs(it.vx);
      }

      if (it.y <= 0) {
        it.y = 0;
        it.vy = Math.abs(it.vy);
      } else if (it.y + it.size >= h) {
        it.y = h - it.size;
        it.vy = -Math.abs(it.vy);
      }
    }

    // repel items that stack from the same row (same size across all types)
    this.resolveSameRowCollisions(w, h);

    // update CSS vars for current positions
    this.applyStyles();

    if (this.running) this.rafId = requestAnimationFrame(this.loop.bind(this));
  }

  private startLoop() {
    if (this.running) return;
    this.lastTime = performance.now();
    this.running = true;
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(this.loop.bind(this));
    });
  }

  private stopLoop() {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  private rebuildElementMap() {
    this.elById.clear();
    this.elItems.forEach((ref) => {
      const el = ref.nativeElement;
      const idAttr = el.getAttribute("data-id");
      if (idAttr) this.elById.set(Number(idAttr), el);
    });
  }

  private applyStyles() {
    if (this.mode !== "js") return;
    for (const it of this.items) {
      const el = this.elById.get(it.id);
      if (!el) continue;
      el.style.setProperty("--tx", it.x + "px");
      el.style.setProperty("--ty", it.y + "px");
    }
  }

  private setModeFromUrl(url: string) {
    const isHome = /^\/?home(\/|$|\?|#)?/i.test(url);
    const desired: "css" | "js" = isHome ? "js" : "css";
    if (desired === this.mode) return;
    this.mode = desired;
    if (this.mode === "js") this.startLoop();
    else this.stopLoop();
  }

  // Only render the largest-row icons in CSS mode; all in JS mode
  public isItemVisible(it: FloatItem): boolean {
    if (this.mode !== "css") return true;
    // when in CSS mode (not on home), only show first row items
    return it.row === 1;
  }

  private getMaxRowSize(): number {
    let max = 0;
    for (const it of this.items) if (it.size > max) max = it.size;
    return max || 0;
  }

  private resolveSameRowCollisions(w: number, h: number) {
    const n = this.items.length;
    for (let i = 0; i < n; i++) {
      const a = this.items[i];
      for (let j = i + 1; j < n; j++) {
        const b = this.items[j];
        // collide all icons within the same row (same size), across all types
        if (a.size !== b.size) continue;

        const r1 = a.size / 2;
        const r2 = b.size / 2;
        const ax = a.x + r1;
        const ay = a.y + r1;
        const bx = b.x + r2;
        const by = b.y + r2;
        let dx = bx - ax;
        let dy = by - ay;
        let dist = Math.hypot(dx, dy);

        const minDist = r1 + r2;
        if (dist >= minDist || minDist === 0) continue;

        if (dist === 0) {
          dx = (Math.random() - 0.5) * 1e-3;
          dy = (Math.random() - 0.5) * 1e-3;
          dist = Math.hypot(dx, dy) || 1e-3;
        }

        const nx = dx / dist;
        const ny = dy / dist;
        const overlap = minDist - dist + 0.5;

        a.x -= nx * (overlap * 0.5);
        a.y -= ny * (overlap * 0.5);
        b.x += nx * (overlap * 0.5);
        b.y += ny * (overlap * 0.5);

        const avi = a.vx * nx + a.vy * ny;
        const bvj = b.vx * nx + b.vy * ny;
        const swap = bvj - avi;
        a.vx += swap * nx;
        a.vy += swap * ny;
        b.vx -= swap * nx;
        b.vy -= swap * ny;

        a.vx *= 0.995;
        a.vy *= 0.995;
        b.vx *= 0.995;
        b.vy *= 0.995;

        if (a.x < 0) a.x = 0;
        if (a.y < 0) a.y = 0;
        if (a.x + a.size > w) a.x = w - a.size;
        if (a.y + a.size > h) a.y = h - a.size;
        if (b.x < 0) b.x = 0;
        if (b.y < 0) b.y = 0;
        if (b.x + b.size > w) b.x = w - b.size;
        if (b.y + b.size > h) b.y = h - b.size;
      }
    }
  }

  ngOnDestroy(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    window.removeEventListener("resize", this.onResize);
    if (this.routeSub) this.routeSub.unsubscribe();
  }
}
